import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";

const projectUrl = `/crm/reports/project_master_schedule`

import {
  initialState,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handleFulfilled
} from "helpers/reducerHelpers";

export const getMasterSchedules = createAsyncThunk(
  "ReportMasterSchedule/getMasterSchedules",
  async (params) => {
    try {
      const response = await apiAxios.get(projectUrl, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const exportData = createAsyncThunk(
  "ReportMasterSchedule/exportData",
  async (params) => {
    const { page, per_page, ...rest } = params
    console.log(rest, page, per_page)

    const response = await apiAxios.get(`${projectUrl}/export`, {params, responseType: 'blob'})
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'master-project-schedule.xlsx');
    document.body.appendChild(link);
    link.click();
  }
)

export const getProjectSummary = createAsyncThunk(
  "ReportMasterSchedule/getProjectSummary",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/project_summary`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getProjectSummaryByYear = createAsyncThunk(
  "ReportMasterSchedule/getProjectSummaryByYear",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/project_summary_by_year`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getContractSummary = createAsyncThunk(
  "ReportMasterSchedule/getContractSummary",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/contract_summary`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getUnderShopDrawing = createAsyncThunk(
  "ReportMasterSchedule/getUnderShopDrawing",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/under_shop`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getWaitingRADToUnderShop = createAsyncThunk(
  "ReportMasterSchedule/getWaitingRADToUnderShop",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/waiting_rad_to_under_shop`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getUnderProduction = createAsyncThunk(
  "ReportMasterSchedule/getUnderProduction",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/under_production`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getUnderShopToUnderProduction = createAsyncThunk(
  "ReportMasterSchedule/getUnderShopToUnderProduction",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/under_shop_to_under_production`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getNewJob = createAsyncThunk(
  "ReportMasterSchedule/getNewJob",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/new_job`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getFinishedShipmentJob = createAsyncThunk(
  "ReportMasterSchedule/getFinishedShipmentJob",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/finished_shipment`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

const isRejectedAction = (action) => (action.type.startsWith("ReportMasterSchedule/")) && action.type.endsWith("/rejected");

const masterScheduleSlice = createSlice({
  name: "report_master_schedule",
  initialState: {
    ...initialState,
    projectSummary: [],
    projectSummaryByYear: [],
    contractSummary: {},
    underShopData: [],
    waitingRADToUnderShopData: {},
    underProductionData: [],
    underShopToUnderProductionData: {},
    newJobData: {},
    finishedShipmentData: {}
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMasterSchedules.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Master schedule has been exported successfully.")
      })
      .addCase(getProjectSummary.fulfilled, (state, action) => {
        state.projectSummary = action.payload
      })
      .addCase(getProjectSummaryByYear.fulfilled, (state, action) => {
        state.projectSummaryByYear = action.payload
      })
      .addCase(getContractSummary.fulfilled, (state, action) => {
        state.contractSummary = action.payload
      })
      .addCase(getUnderShopDrawing.fulfilled, (state, action) => {
        state.underShopData = action.payload
      })
      .addCase(getWaitingRADToUnderShop.fulfilled, (state, action) => {
        state.waitingRADToUnderShopData = action.payload
      })
      .addCase(getUnderProduction.fulfilled, (state, action) => {
        state.underProductionData = action.payload
      })
      .addCase(getUnderShopToUnderProduction.fulfilled, (state, action) => {
        state.underShopToUnderProductionData = action.payload
      })
      .addCase(getNewJob.fulfilled, (state, action) => {
        state.newJobData = action.payload
      })
      .addCase(getFinishedShipmentJob.fulfilled, (state, action) => {
        state.finishedShipmentData = action.payload
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = masterScheduleSlice.actions;

export default masterScheduleSlice.reducer;